/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { CSSProperties } from 'react';
import { cn } from '@/lib/utils';
import { getTextStyle } from '@/lib/page-designer/style-attributes';

// CarouselSection renders its own <h2> and only accepts a className, so authored font/color are
// passed as CSS variables on a wrapper and picked up by arbitrary-value classes on the title.
const ALIGN_TITLE_CLASSES: Record<string, string> = {
    Left: 'flex-1 text-left',
    Center: 'flex-1 text-center',
    Right: 'flex-1 text-right',
};
const ALIGN_WRAPPER_CLASSES: Record<string, string> = {
    Left: '[&_h2+p]:text-left [&_h2+p]:ml-0',
    Center: '[&_h2+p]:text-center',
    Right: '[&_h2+p]:text-right [&_h2+p]:mr-0',
};

export interface CarouselTitleStyleInput {
    headingFontStyle?: string;
    headingFontSize?: string;
    headingColor?: string;
    textAlign?: string;
}

/**
 * Builds the CarouselSection `titleClassName` plus wrapper class/style for Page Designer title styling.
 * Returns an undefined titleClassName when nothing is authored so CarouselSection keeps its default.
 */
export function getCarouselTitleStyle(
    { headingFontStyle, headingFontSize, headingColor, textAlign }: CarouselTitleStyleInput,
    defaultTitleClassName: string
): { titleClassName?: string; wrapperClassName?: string; wrapperStyle?: CSSProperties } {
    const { className: sizeClassName, style } = getTextStyle(
        { fontStyle: headingFontStyle, fontSize: headingFontSize, color: headingColor },
        'heading'
    );
    const alignTitle = textAlign ? ALIGN_TITLE_CLASSES[textAlign] : undefined;
    if (!sizeClassName && !alignTitle && !style.fontFamily && !style.color) {
        return {};
    }
    const wrapperStyle: Record<string, string> = {};
    if (style.fontFamily) wrapperStyle['--pd-title-font'] = style.fontFamily;
    if (style.color) wrapperStyle['--pd-title-color'] = style.color;
    return {
        titleClassName: cn(
            defaultTitleClassName,
            sizeClassName,
            alignTitle,
            style.fontFamily && '[font-family:var(--pd-title-font)]',
            style.color && '[color:var(--pd-title-color)]'
        ),
        wrapperClassName: textAlign ? ALIGN_WRAPPER_CLASSES[textAlign] : undefined,
        wrapperStyle: Object.keys(wrapperStyle).length ? (wrapperStyle as CSSProperties) : undefined,
    };
}
